/**
 * `item_id` — `ItemIdValue` (`specs/M2-tier2-column-types.md` §5.13, `[09§A.7 item_id]`).
 *
 * *"read-only, auto-calculated column. It does not support update, clear, or set-on-creation
 * operations."* The value is the item's own id: `{"item_id":"13009113115"}`, `text` the same
 * digits — 2,063 of 2,063 sampled cells populated, all equal to `Item.id` `[snap]`.
 *
 * Nothing is stored (`materialise: 'never'`): a row per item holding the item's id would be a
 * second copy of the primary key. It is projected on read from `ComputeContext.item.id`.
 */
import { z } from 'zod';
import { DEFAULT_SQL_REFS, defaultFilterSql, defaultSummarySql, sqlFragment } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const itemIdValueSchema = z.looseObject({ item_id: z.string() });
export const itemIdSettingsSchema = z.looseObject({
    /** `text` | `link` — what the cell's copy button puts on the clipboard `[09§A.7]`. */
    copyValueSetting: z.string().optional(),
    /** `pulseId` | `customKey`. */
    displayType: z.string().optional(),
    customKeySettings: z.looseObject({ prefix: z.string().optional(), padding: z.number().optional() }).optional(),
    hide_footer: z.boolean().optional(),
});
import { itemIdFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** `customKey` display: `settings.customKeySettings` prefixes and left-pads the id. */
export function itemIdDisplay(value, settings) {
    if (value === null)
        return '';
    if (settings.displayType !== 'customKey')
        return value.item_id;
    const prefix = settings.customKeySettings?.prefix ?? '';
    const padding = settings.customKeySettings?.padding ?? 0;
    return `${prefix}${value.item_id.padStart(padding, '0')}`;
}
function coerce(raw) {
    if (raw === null || raw === undefined)
        return null;
    const id = typeof raw === 'object' ? raw.item_id : raw;
    if (id === null || id === undefined || id === '')
        return null;
    return { item_id: String(id) };
}
export const itemIdColumn = defineColumnType({
    type: 'item_id',
    title: 'Item ID',
    description: "The item's id. Read-only.",
    apiFragmentName: 'ItemIdValue',
    legacyTypeName: 'pulse-id',
    idPrefix: 'item_id',
    valueSchema: itemIdValueSchema,
    settingsSchema: itemIdSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: itemIdSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    filterConditions: itemIdFilterConditions,
    filterOperators: operatorsOf(itemIdFilterConditions),
    aggregations: ['count', 'none'],
    /** Never empty: every item has an id. */
    emptyText: '',
    computed: {
        dependsOn() {
            return [{ kind: 'item_meta', field: 'id' }];
        },
        compute(ctx) {
            const id = ctx.item?.id;
            return id === undefined || id === null ? null : { item_id: String(id) };
        },
        materialise: 'never',
        api: { value: 'stored', text: 'stored' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('item_id', 'the item id column is read-only');
    },
    toText(value, settings) {
        return itemIdDisplay(value, settings);
    },
    toSortKeys(value, settings) {
        if (value === null)
            return { text: null, number: null, date: null };
        const numeric = Number(value.item_id);
        return {
            text: textShadow(itemIdDisplay(value, settings)),
            number: Number.isFinite(numeric) ? numeric : null,
            date: null,
        };
    },
    isEqual(a, b) {
        return (a?.item_id ?? null) === (b?.item_id ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { item_id: value.item_id };
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value === null ? null : { item_id: value.item_id };
    },
    toSimpleValue(value) {
        return value === null ? null : value.item_id;
    },
    fromMondaySettings(raw) {
        return itemIdSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        // The fragment carries the **raw** id even under `customKey` display `[cvs§2 item_id]`.
        return { item_id: value.item_id };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /**
     * **Projected, so the SQL reads `items.id` (T-9p).** No `cell_values` row exists for this
     * column, so the text shadow is null for every item: `is one of` matched nothing and — worse
     * — `is empty` (`cv.item_id IS NULL`) matched *everything*, where monday's own answer is "always
     * matches nothing", because every item has an id.
     */
    filterSql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        if (ctx.operator === 'is_empty')
            return sqlFragment('false');
        if (ctx.operator === 'is_not_empty')
            return sqlFragment('true');
        return defaultFilterSql('text', ctx, `${refs.item}.id::text`);
    },
    sortSql(refs) {
        return [`${refs.item}.id`];
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map